import { useCallback, useEffect } from 'react';

import LogoHorizontal from '@assets/logo-horizontal.png';
import { Button } from '@components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { ScrollArea } from '@components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { pages } from '@constants/pages';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import formatterOnlyNumber from '@formatters/onlyNumbers';
import { zodResolver } from '@hookform/resolvers/zod';
import CustomerService from '@services/customers/CustomerService';
import { type ICreateCustomerInputData } from '@services/customers/dtos/createCustomer/InputData';
import { type IGetAllOccupationAreasOutputData } from '@services/occupationAreas/dtos/getAllOccupationAreas/OutputData';
import OccupationAreaService from '@services/occupationAreas/OccupationAreaService';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

declare const window: any;

const validatePhone = (value: string) => {
  return value.replace(/[^0-9]/g, '').length === 11;
};

const formSchema = z.object({
  name: z.string().min(1, {
    message: errorsValidation.required,
  }),
  phone: z.string().refine((val) => validatePhone(val), errorsValidation.phone),
  areaActivity: z.string().min(1, {
    message: errorsValidation.required,
  }),
  email: z.email(errorsValidation.email).min(1, {
    message: errorsValidation.required,
  }),
  password: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function SignUp() {
  const { authorization } = useAuth();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      phone: '',
      areaActivity: '',
      email: '',
      password: '',
    },
  });

  const { data: occupationAreas } = useQuery<IGetAllOccupationAreasOutputData>({
    queryFn: async () => {
      const response = await OccupationAreaService.getAll();
      return response;
    },
    queryKey: ['occupationArea'],
    refetchOnWindowFocus: false,
  });

  const { isPending, mutateAsync } = useMutation<
    void,
    unknown,
    ICreateCustomerInputData
  >({
    mutationFn: CustomerService.create,
    onError: () => {
      toast.error('Atenção', {
        description: 'Não foi possível criar a conta. Tente novamente',
      });
    },
    onSuccess: () => {
      toast.success('Parabéns!', {
        description: 'Sua conta foi criada com sucesso!',
      });
      navigate(pages.auth.signIn, { replace: true });
    },
  });

  const handleOnSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      await mutateAsync({
        email: values.email,
        name: values.name,
        occupationAreaId: Number(values.areaActivity),
        password: values.password,
        phone: formatterOnlyNumber(values.phone),
      });
    },
    [mutateAsync],
  );

  useEffect(() => {
    if (authorization.token) {
      navigate(pages.home, { replace: true });
    }
  }, [authorization.token, navigate]);

  useEffect(() => {
    // TODO: Desativar em modo dev
    if (!window.fbq) {
      (function (f: any, b, e, v) {
        if (f.fbq) {
          return;
        }

        const n: any = (f.fbq = function () {
          if (n.callMethod) {
            // eslint-disable-next-line prefer-spread, prefer-rest-params
            n.callMethod.apply(n, arguments);
          } else {
            // eslint-disable-next-line prefer-rest-params
            n.queue.push(arguments);
          }
        });
        if (!f._fbq) {
          f._fbq = n;
        }
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];

        const t: any = b.createElement(e);
        t.async = !0;
        t.src = v;

        const s: any = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        'script',
        'https://connect.facebook.net/en_US/fbevents.js',
      );
      window.fbq('init', '937559308050764');
      window.fbq('track', 'PageView');
    }
  }, []);

  return (
    <ScrollArea className="bg-primary h-dvh">
      <main className="flex min-h-dvh w-full flex-col items-center justify-center p-4 sm:py-8">
        <Card className="bg-primary border-background w-full max-w-md">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleOnSubmit)}>
              <CardHeader>
                <img
                  alt="Logo GPMED"
                  className="mx-auto mb-8 w-3/4"
                  src={LogoHorizontal}
                />
                <CardTitle className="text-background text-3xl font-semibold">
                  Cadastro
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-background">
                            Nome
                          </FormLabel>
                          <FormMessage />
                        </div>
                        <FormControl>
                          <Input
                            className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background"
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field: { ref, ...rest } }) => (
                      <FormItem>
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-background">
                            Celular
                          </FormLabel>
                          <FormMessage />
                        </div>
                        <FormControl>
                          <PatternFormat
                            autoComplete="off"
                            className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background"
                            customInput={Input}
                            format="(##) #####-####"
                            getInputRef={ref}
                            mask="_"
                            {...rest}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="areaActivity"
                    render={({ field }) => (
                      <FormItem className="flex flex-col justify-end">
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-background">
                            Area em que atua
                          </FormLabel>
                          <FormMessage />
                        </div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}>
                          <FormControl>
                            <SelectTrigger
                              className="text-background [&_svg:not([class*='text-'])]:text-background [&_svg]:opacity-100"
                              onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>

                          <SelectContent>
                            {occupationAreas?.list.map((area) => (
                              <SelectItem
                                key={area.id}
                                value={area.id.toString()}>
                                {area.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-background">
                            E-mail
                          </FormLabel>
                          <FormMessage />
                        </div>
                        <FormControl>
                          <Input
                            className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background"
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex flex-row justify-between">
                          <FormLabel className="text-background">
                            Senha
                          </FormLabel>
                          <FormMessage />
                        </div>
                        <FormControl>
                          <Input
                            {...field}
                            className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background [&_button]:text-background"
                            secure
                            type="password"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <p className="text-background mt-8 text-center text-sm font-light">
                  Ao clicar no botão "CADASTRAR", você reconhece que leu e
                  aceita os{' '}
                  <Link
                    className="text-secondary underline"
                    target="_blank"
                    to={pages.terms}>
                    termos de uso
                  </Link>{' '}
                  e{' '}
                  <Link
                    className="text-secondary underline"
                    target="_blank"
                    to={pages.privacyPolicies}>
                    políticas de privacidade
                  </Link>{' '}
                  da GPMED.
                </p>
              </CardContent>
              <CardFooter className="grid">
                <Button disabled={isPending} type="submit" variant="secondary">
                  {isPending ? (
                    <LoaderCircle className="text-background animate-spin" />
                  ) : (
                    'CADASTRAR'
                  )}
                </Button>

                <Link
                  className="text-background mt-4 text-center tracking-wider underline"
                  to={pages.auth.signIn}>
                  Voltar
                </Link>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </main>
    </ScrollArea>
  );
}

export default SignUp;
